import { Button } from "../../../../components/Button";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { OrganizationMember } from "../../../../types/api";
import type { TournamentGameDto } from "../../../../types/tournament-games";
import {
  getTournamentGames,
  setGameAssignees,
  updateGame,
  deleteGame,
} from "../../../../services/tournament-games";
import { utcToZonedInput, zonedInputToUtcIso } from "../../../../utils/calendarTime";
import { getSafeErrorMessage } from "../../../../utils/errorHandler";
import { CoachSelectField, TeamNameField } from "./ScheduleFields";
import { useDismiss } from "./useDismiss";
import { Avatar, PlusIcon } from "./CoachAvatar";
import { formatGameDateTime, formatMatchup } from "./gameDisplay";
import { GamePitchersRow } from "./GamePitchersRow";
import { coverageSummaryText, summarizeCoverage } from "./coverageSummary";
import { PrintScheduleLink } from "./print/PrintScheduleLink";
import { isFollowedOnGamesTab } from "./print/schedulePrint";

interface GameRowProps {
  calendarEventId: string;
  game: TournamentGameDto;
  members: OrganizationMember[];
  currentUserId?: string;
  /** Teams the viewer follows, so the row can flag a followed team on either side. */
  followedTeamIds: ReadonlySet<string>;
  onSaved: (updated: TournamentGameDto) => void;
  /** Edit/delete return the full refreshed list (span/ordering may shift), replacing panel state. */
  onGamesReplaced: (games: TournamentGameDto[]) => void;
}

/** One game: its time/field/matchup, effective coverage, and a coach picker. */
const GameRow = ({
  calendarEventId,
  game,
  members,
  currentUserId,
  followedTeamIds,
  onSaved,
  onGamesReplaced,
}: GameRowProps) => {
  const homeFollowed = game.homeTeamId != null && followedTeamIds.has(game.homeTeamId);
  const awayFollowed = game.awayTeamId != null && followedTeamIds.has(game.awayTeamId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // The coach picker is hidden behind a "+" next to the covering avatars.
  const [pickerOpen, setPickerOpen] = useState(false);
  const coverRef = useDismiss<HTMLSpanElement>(pickerOpen, () => setPickerOpen(false));
  // Selected ids derive from the parent-owned game (controlled). While a save is
  // in flight we show an optimistic override so the picker stays responsive.
  const [pending, setPending] = useState<string[] | null>(null);
  // Monotonic request id: only the newest response is allowed to win, so rapid
  // toggles or out-of-order responses can't clobber each other or roll back stale.
  const reqSeq = useRef(0);

  // Inline edit (fix a mis-parsed game) + delete (drop a phantom game).
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editError, setEditError] = useState("");
  const [form, setForm] = useState({
    startLocal: "",
    fieldName: "",
    home: "",
    away: "",
  });

  const openEdit = () => {
    setForm({
      startLocal: utcToZonedInput(game.startsAtUtc, game.timeZone),
      fieldName: game.fieldName,
      home: game.homeTeamNameRaw,
      away: game.awayTeamNameRaw,
    });
    setEditError("");
    setEditing(true);
  };

  const saveEdit = async () => {
    setBusy(true);
    setEditError("");
    try {
      const resp = await updateGame(calendarEventId, game.id, {
        startsAtUtc: zonedInputToUtcIso(form.startLocal, game.timeZone),
        fieldName: form.fieldName.trim(),
        homeTeamNameRaw: form.home.trim(),
        awayTeamNameRaw: form.away.trim(),
        expectedUpdatedAt: game.updatedAt,
      });
      onGamesReplaced(resp.games);
      setEditing(false);
    } catch (err) {
      setEditError(
        getSafeErrorMessage(err, "Couldn't save the game. Reload and try again."),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Remove this game from the schedule? This can't be undone.")) return;
    setBusy(true);
    setEditError("");
    try {
      const resp = await deleteGame(calendarEventId, game.id);
      onGamesReplaced(resp.games);
    } catch (err) {
      setEditError(getSafeErrorMessage(err, "Couldn't remove the game. Try again."));
      setBusy(false);
    }
  };

  const selectedIds = pending ?? game.assignees.map((a) => a.userId);
  const dateTime = formatGameDateTime(game);
  // "You're covering" derived optimistically so it tracks the per-game toggle
  // immediately (no flicker waiting on the save): you're covering if you're a
  // per-game assignee (optimistic selectedIds) OR an event-level assignee
  // (isAssignedToMe true but not in the game's own assignees).
  const coveringViaEvent =
    game.isAssignedToMe && !game.assignees.some((a) => a.userId === currentUserId);
  const isCovering =
    currentUserId != null && (selectedIds.includes(currentUserId) || coveringViaEvent);

  const handleChange = async (next: string[]) => {
    const seq = ++reqSeq.current;
    setPending(next); // optimistic
    setSaving(true);
    setError("");
    try {
      const updated = await setGameAssignees(calendarEventId, game.id, next);
      if (seq !== reqSeq.current) return; // a newer change superseded this one
      onSaved(updated);
      setPending(null); // fall back to the parent-owned game
    } catch {
      if (seq !== reqSeq.current) return;
      setPending(null); // revert to the last saved game
      setError("Couldn't save game coaches. Try again.");
    } finally {
      if (seq === reqSeq.current) setSaving(false);
    }
  };

  return (
    <div
      className={`game-row${isCovering ? " game-row--mine" : ""}`}
      data-testid={`game-row-${game.id}`}
    >
      <div className="game-row__head">
        <div className="game-row__headmain">
          {!editing && (
            <div className="game-row__matchup">{formatMatchup(game)}</div>
          )}
          <div className="game-row__meta">
            <span className="game-row__when">{dateTime}</span>
            <span className="game-row__meta-dot" aria-hidden="true">
              ·
            </span>
            <span className="game-row__field">{game.fieldName}</span>
          </div>
        </div>
        {!editing && (
          <div className="game-row__edit-actions">
            <button
              type="button"
              className="game-row__edit-btn"
              onClick={openEdit}
              disabled={busy}
              data-testid={`game-edit-${game.id}`}
            >
              Edit
            </button>
            <button
              type="button"
              className="game-row__edit-btn game-row__edit-btn--danger"
              onClick={handleDelete}
              disabled={busy}
              data-testid={`game-delete-${game.id}`}
            >
              {busy ? "…" : "Delete"}
            </button>
          </div>
        )}
      </div>

      {editing && (
        <div className="game-row__edit" data-testid={`game-edit-form-${game.id}`}>
          <label className="game-row__edit-field">
            <span>Start ({game.timeZone})</span>
            <input
              type="datetime-local"
              value={form.startLocal}
              onChange={(e) => setForm((f) => ({ ...f, startLocal: e.target.value }))}
            />
          </label>
          <span className="game-row__edit-field" id="tournament-games-panel-field-setform-f-placeholder-field--0">
            <span>Field</span>
            <input
              type="text"
              value={form.fieldName}
              onChange={(e) => setForm((f) => ({ ...f, fieldName: e.target.value }))}
              placeholder="Field / venue"
            />
          </span>
          <div className="game-row__edit-teams" role="group" aria-labelledby="tournament-games-panel-field-setform-f-placeholder-field--0">
            <label className="game-row__edit-field">
              <span>Home</span>
              <TeamNameField
                value={form.home}
                onChange={(name) => setForm((f) => ({ ...f, home: name }))}
                ariaLabel="Home team"
              />
            </label>
            <label className="game-row__edit-field">
              <span>Away</span>
              <TeamNameField
                value={form.away}
                onChange={(name) => setForm((f) => ({ ...f, away: name }))}
                ariaLabel="Away team"
              />
            </label>
          </div>
          <div className="game-row__edit-buttons">
            <Button variant="secondary"
              type="button"
              onClick={() => setEditing(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button variant="primary"
              type="button"
              onClick={saveEdit}
              disabled={busy}
              data-testid={`game-edit-save-${game.id}`}
            >
              {busy ? "Saving…" : "Save game"}
            </Button>
          </div>
          {editError && (
            <div className="event-editor__error" role="alert">
              {editError}
            </div>
          )}
        </div>
      )}

      {(homeFollowed || awayFollowed || game.matchedPlayers.length > 0) && (
        <div className="game-row__following" data-testid={`game-following-${game.id}`}>
          <span className="game-row__following-label">Following:</span>
          {homeFollowed && (
            <span className="game-row__follow-chip game-row__follow-chip--team">
              {game.homeTeamNameRaw || "Home"} (team)
            </span>
          )}
          {awayFollowed && (
            <span className="game-row__follow-chip game-row__follow-chip--team">
              {game.awayTeamNameRaw || "Away"} (team)
            </span>
          )}
          {game.matchedPlayers.map((p) => (
            <span key={p.prospectId} className="game-row__follow-chip">
              {p.name}
            </span>
          ))}
        </div>
      )}

      <GamePitchersRow calendarEventId={calendarEventId} game={game} onSaved={onSaved} />

      <div className="game-row__coverage">
        <span className="game-row__coverage-label">Covering:</span>
        <span className="game-row__avatars" ref={coverRef}>
          {/* Removal is done by unchecking in the "+" coach list, so avatars are
              plain (no hover-x). */}
          {game.effectiveCoaches.map((c) => (
            <Avatar
              key={c.userId}
              id={c.userId}
              initials={c.initials}
              ring={c.userId === currentUserId}
              title={c.displayName}
            />
          ))}
          <button
            type="button"
            className={`sf-avatar game-row__add-coach${pickerOpen ? " game-row__add-coach--on" : ""}`}
            onClick={() => setPickerOpen((v) => !v)}
            aria-expanded={pickerOpen}
            aria-label="Add a coach"
            title="Add a coach"
            data-testid={`game-add-coach-${game.id}`}
          >
            <PlusIcon />
          </button>
          {/* The coach list drops as an absolute overlay anchored to the avatars
              area, so opening it never grows the card's height. */}
          {pickerOpen && (
            <CoachSelectField
              members={members}
              value={selectedIds}
              onChange={handleChange}
              currentUserId={currentUserId}
              menuOnly
            />
          )}
        </span>
        {game.effectiveCoaches.length === 0 && (
          <span className="game-row__coverage-empty">No coaches yet</span>
        )}
        {isCovering && (
          <span className="game-row__mine-badge" data-testid={`game-mine-${game.id}`}>
            You're covering
          </span>
        )}
        {saving && (
          <span className="game-row__status" aria-live="polite">
            Saving…
          </span>
        )}
      </div>

      {error && (
        <div className="event-editor__error" role="alert">
          {error}
        </div>
      )}
      {!editing && editError && (
        <div className="event-editor__error" role="alert">
          {editError}
        </div>
      )}
    </div>
  );
};

interface TournamentGamesPanelProps {
  /** The saved Tournament event id. */
  calendarEventId: string;
  /** Org members for the coach picker (shared with the tournament-level picker). */
  members: OrganizationMember[];
  currentUserId?: string;
  /** Notify the parent that games/assignments changed so the calendar can refetch
   *  (per-game assignments feed the "My events" view) without a manual refresh. */
  onChanged?: () => void;
  /** Bump to force a refetch — e.g. after an event-level assignee change above,
   *  which alters every game's effective coverage. */
  reloadToken?: number;
}

/**
 * Per-game coach assignment for a saved Tournament. Lists the tournament's
 * games and lets staff add coaches to individual games. Game coaches are
 * additive: they are added to the tournament's coaches for that game, never
 * replacing them.
 */
export const TournamentGamesPanel = ({
  calendarEventId,
  members,
  currentUserId,
  onChanged,
  reloadToken,
}: TournamentGamesPanelProps) => {
  const headingId = useId();
  const [games, setGames] = useState<TournamentGameDto[]>([]);
  // Teams the current user follows (server-derived per read). A game is "followed"
  // when either side is a followed team OR it has a followed player (matchedPlayers).
  const [followedTeamIds, setFollowedTeamIds] = useState<ReadonlySet<string>>(new Set());
  // Filter: show only games the user follows (via team or player) vs all games.
  // Defaults to Followed (the point of an itinerary is your games first), but falls
  // back to All when the user follows nothing here — an empty Followed tab is useless.
  const [followedOnly, setFollowedOnly] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  // The event the Followed/All default was applied for, so a later reload (e.g. after
  // assigning a coach) never overrides the user's manual tab choice on the same event.
  const defaultedForRef = useRef<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    getTournamentGames(calendarEventId)
      .then((resp) => {
        if (active) {
          setGames(resp.games);
          const followed = new Set(resp.followedTeamIds ?? []);
          setFollowedTeamIds(followed);
          // First load for this event: if nothing here is followed, start on All.
          if (defaultedForRef.current !== calendarEventId) {
            const hasFollowed = resp.games.some((g) =>
              isFollowedOnGamesTab(g, followed, currentUserId),
            );
            setFollowedOnly(hasFollowed);
            defaultedForRef.current = calendarEventId;
          }
        }
      })
      .catch(() => {
        if (active) setLoadError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [calendarEventId, reloadToken, currentUserId]);

  const sortedGames = useMemo(
    () => [...games].sort((a, b) => a.sortOrder - b.sortOrder),
    [games],
  );

  // A game is followed when I am one of its coaches, a followed player is on it,
  // or either side is a followed team. Backs the "Followed / All" filter, and the
  // printout of that tab uses the same function so the two cannot disagree.
  const isFollowedGame = (g: TournamentGameDto): boolean =>
    isFollowedOnGamesTab(g, followedTeamIds, currentUserId);

  const followedCount = useMemo(
    () => sortedGames.filter(isFollowedGame).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortedGames, followedTeamIds, currentUserId],
  );
  const shownGames = followedOnly ? sortedGames.filter(isFollowedGame) : sortedGames;

  const coverageText = useMemo(
    () => coverageSummaryText(summarizeCoverage(sortedGames)),
    [sortedGames],
  );

  const applyUpdate = (updated: TournamentGameDto) => {
    setGames((prev) => prev.map((g) => (g.id === updated.id ? updated : g)));
    // A per-game coach change feeds the calendar's "My events" — refresh it.
    onChanged?.();
  };

  // Edit/delete return the refreshed list; replace state and refresh the calendar.
  const handleGamesReplaced = (next: TournamentGameDto[]) => {
    setGames(next);
    onChanged?.();
  };

  return (
    <div
      className="event-editor__field games-panel"
      data-testid="tournament-games-panel"
      role="group"
      aria-labelledby={headingId}
    >
      <div id={headingId} className="games-panel__heading">
        Games
      </div>
      <div className="event-editor__hint">
        Game coaches are added to the tournament's coaches. To split coverage,
        assign coaches per game and leave them off the tournament.
      </div>

      <div aria-live="polite">
        {loading && <div className="event-editor__hint">Loading games…</div>}
        {loadError && (
          <div className="event-editor__error" role="alert">
            Couldn't load the games. Try reopening the event.
          </div>
        )}
        {!loading && !loadError && sortedGames.length === 0 && (
          <div className="event-editor__hint">
            No games yet. Add games with the auto-fill schedule tool above.
          </div>
        )}
        {!loading && !loadError && sortedGames.length > 0 && coverageText && (
          <p className="games-panel__coverage" data-testid="games-coverage-summary">
            {coverageText}
          </p>
        )}
      </div>

      {!loading && !loadError && sortedGames.length > 0 && (
        <>
          <div
            className="games-panel__filter"
            role="group"
            aria-label="Filter games"
            data-testid="games-filter"
          >
            <button
              type="button"
              className={`games-panel__filter-btn${followedOnly ? " games-panel__filter-btn--on" : ""}`}
              aria-pressed={followedOnly}
              onClick={() => setFollowedOnly(true)}
              data-testid="games-filter-followed"
            >
              Followed ({followedCount})
            </button>
            <button
              type="button"
              className={`games-panel__filter-btn${!followedOnly ? " games-panel__filter-btn--on" : ""}`}
              aria-pressed={!followedOnly}
              onClick={() => setFollowedOnly(false)}
              data-testid="games-filter-all"
            >
              All games ({sortedGames.length})
            </button>
          </div>
          {/* Prints the tab on screen: Followed or All games. */}
          <PrintScheduleLink
            request={{
              kind: "event",
              eventId: calendarEventId,
              games: followedOnly ? "followed" : "all",
            }}
            className="games-panel__print"
            data-testid="games-print"
          />

          {shownGames.length === 0 ? (
            <div className="event-editor__hint" data-testid="games-followed-empty">
              You are not following any teams or players in this event yet. Follow a team
              or star a player to build your itinerary.
            </div>
          ) : (
            <div className="games-list">
              {shownGames.map((game) => (
                <GameRow
                  key={game.id}
                  calendarEventId={calendarEventId}
                  game={game}
                  members={members}
                  currentUserId={currentUserId}
                  followedTeamIds={followedTeamIds}
                  onSaved={applyUpdate}
                  onGamesReplaced={handleGamesReplaced}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
